import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';
import { GridCol, GridPageLayout, GridRow, Grid as LUXGrid } from '@lux/grid';
import { FunctionColor, Tag, TitlePreset } from '@lux/title';
import { Preset as TextPreset } from '@lux/text';
import { FlashMessage } from '@pegasus/ui-flashmessages';
import { useIsEligibleForVoucher } from '@pegasus/platform-journeys';
import {
    LoyaltyOnboardingScreen,
    LoyaltyStatus,
    PlusContentDialog,
    PlusContentDialogType,
    useLoyaltyOnboardingScreen,
    usePlusContentDialog,
} from '@pegasus/plus';
import { Translate, TranslateScope, useTranslate } from '@pegasus/translation';
import { BottomBubble, ErrorBoundary } from '@browsing/components';
import { useConfig } from '@browsing/config';
import { useQuery, usePlusExclusive } from '@browsing/hooks';
import { Message, MessageType } from '@lux/message';

import { MYLOUNGE_ROUTE } from '@client/config/constants';
import { CategoryTabIds, CSRF_HEADER, XSRF } from '@shared/constants';
import { RootState } from '@client/redux/types';
import { getIsMobileScreen } from '@client/redux/modules/clientDevice/selectors';
import {
    getCustomer,
    getHasEmailConfirmed,
    State as CustomerState,
    getGender,
    fetchCustomer,
} from '@client/redux/modules/customer';
import {
    getCategoryTabId,
    getOpenCampaigns,
    getTabs,
    makeGetUpcomingCampaigns,
} from '@client/redux/modules/mylounge';
import { useGRE } from '@client/components/mylounge/hooks';
import { incrementPageVisitsCount } from '@client/helpers';

import { getTrackingData } from '@client/redux/modules/tracking/selectors';
import {
    trackSelectContent,
    trackViewContent,
    useTrackCampaignsImpression,
    trackViewPromotion,
} from '@browsing/tracking';

import { useInView } from 'react-intersection-observer';
import { HighlightsCarousel } from '@browsing/highlights';
import { Button, ButtonColor, ButtonType } from '@lux/buttons';
import { useLocation, useNavigate } from 'react-router-dom';

import { sleep } from '@pegasus/utils';
import { getCookie, ONE_DAY, ONE_HOUR, setCookie } from '@client/helpers/cookie';
import { RestClient } from '@pegasus/rest-client';
import { getCSRFCookie } from '@browsing/utils';
import { showHeaderFlashMessage } from '@client/redux/modules/flashMessage/actions';
import { AccessibleHeader } from '../shared/accessible-header';
import Banner from './banner/banner';
import { Grid } from './campaigns/grid/Grid';
import UpcomingSection from './campaigns/upcoming/upcoming-section-wrapper';
import { convertFlashMessageToAlertType } from './helpers';
import {
    CAMPAIGN_WRAPPER,
    CONTINUE_SHOPPING_HASH,
    EXPLORE_YOUR_REWARDS_HASH,
    FREE_DELIVERY_COOKIE,
    FREE_DELIVERY_SESSION_COOKIE,
    GRANTED,
    LATER_HASH,
    LOYALTY_OPT_IN_ONBOARDING_DIALOG_COOKIE,
    PLUS_HASH,
    TERMS_AND_CONDITIONS_HASH,
    UPCOMING_CAMPAIGNS_WRAPPER,
} from './constants';
import { CampaignExpiredStatus, ExpiredCampaignDialog } from './expired-campaign-dialog';
import { CrossCampaignEntrySmallScreen, useCrossCampaignEntry } from './cross-campaign';
import { FreeDeliveryBenefitsDialog, useZalandoPlus } from './plus';

import * as S from './mylounge-wrapper.styles';
import { GREDescription, GRETitle, GRECarousel } from './gre';
import { useLoyalty } from './hooks/use-loyalty';
import { useIsPlatformJourney } from './platform-journeys';

import { EACarousel } from './plus/ea-carousel';

type FlashMessageWithText = FlashMessage & {
    text: string;
    title: string;
};

interface Props {
    flashMessages?: FlashMessage[];
}

const MyLoungeWrapper: FunctionComponent<Props> = ({ flashMessages }) => {
    const query = useQuery();
    const plus = useZalandoPlus();
    const isMobileScreen = useSelector(getIsMobileScreen);
    const tabs = useSelector(getTabs);
    const hasPlusOffersTab = tabs.some(tab => tab.event === 'PLUS_OFFER_EVENT');
    const openCampaigns = useSelector(getOpenCampaigns);
    const isMailConfirmed = useSelector(getHasEmailConfirmed);
    const location = useLocation();
    const isPlusExclusiveEnabled = usePlusExclusive();
    const isPlatformJourneyEnabled = useIsPlatformJourney();
    const isEligibleForVoucher = useIsEligibleForVoucher();
    const { showCrossCampaignFilterInMobile } = useCrossCampaignEntry();
    const translate = useTranslate('myLounge');

    const navigate = useNavigate();
    const dispatch = useDispatch();

    const customer = useSelector(getCustomer);

    const tracking = useSelector(getTrackingData);
    const categoryTabId = useSelector(getCategoryTabId);

    useTrackCampaignsImpression({
        categoryTabId,
        isMobileScreen,
        visitorID: tracking?.visitorID,
    });

    useEffect(() => {
        incrementPageVisitsCount('zl_mylounge_page_view_count');
    }, []);

    const getUpcomingCampaigns = useMemo(makeGetUpcomingCampaigns, []);
    const upcomingCampaigns = useSelector((state: RootState) =>
        getUpcomingCampaigns(state, {
            isPlusEnabled: plus.isEnabled,
            isPlusExclusiveEnabled,
        }),
    );

    const config = useConfig();
    const isPrive = config.isPriveDomain;
    const fs_link = config.fashion_store_link;

    const isEACarouselEnabled =
        config.isEnabled('cw_ea_open_campaign_carousel') ||
        getCookie('zl_cw_ea_campaign_carousel') === 'true';
    const tabName = tabs?.find(t => t.id === categoryTabId)?.name;
    const customerGender = useSelector(getGender)?.toUpperCase();
    const tabGender = tabs?.find(t => t.id === categoryTabId)?.gender;

    const gender = (tabGender || customerGender || 'FEMALE') as 'MALE' | 'FEMALE' | 'UNISEX';

    const [shouldShowLoyaltyOnboardingScreen, loyaltyOnboardingScreenShown] =
        useLoyaltyOnboardingScreen(config.appdomain);
    const [dialogType, dialogUrl, markDialogAsShown] = usePlusContentDialog(true);
    const loyalty = useLoyalty();

    // Free Delivery benefit logic
    const hasFreeDeliveryBenefit = useMemo(() => {
        return loyalty?.summary?.benefits?.some(benefit => benefit.name === 'FREE_DELIVERY');
    }, [loyalty?.summary?.status]);

    const minimumOrderValue = useMemo(() => {
        const isFreeDelivery = loyalty?.summary?.benefits?.filter(benefit => {
            return benefit.name === 'FREE_DELIVERY';
        });

        if (isFreeDelivery?.length > 0) {
            return isFreeDelivery[0].minimumOrderValue;
        }
    }, [loyalty?.summary?.status]);

    const isLoyaltyMember = loyalty?.summary?.status === LoyaltyStatus.Active;
    const isLoyaltyEligible = loyalty?.summary?.status === LoyaltyStatus.Eligible;
    const noOtherModalIsShown = useMemo(() => {
        return (
            !isPlatformJourneyEnabled &&
            !isEligibleForVoucher &&
            (isMailConfirmed || !location.search)
        );
    }, [isPlatformJourneyEnabled, isEligibleForVoucher, isMailConfirmed]);

    // Show free delivery modal logic - customize this condition based on your requirements
    const shouldShowFreeDeliveryModal = useMemo(() => {
        const hasSeenModalRecently = getCookie(FREE_DELIVERY_COOKIE) === 'true';
        const showFDModal =
            noOtherModalIsShown &&
            hasFreeDeliveryBenefit &&
            ((isLoyaltyMember && !dialogType) || isLoyaltyEligible) &&
            !hasSeenModalRecently;

        return showFDModal;
    }, [
        dialogType,
        noOtherModalIsShown,
        hasFreeDeliveryBenefit,
        isLoyaltyMember,
        isLoyaltyEligible,
    ]);

    const shouldShowLoyaltyOnboardingModal = useMemo(() => {
        const hasSeenOnboardingModalRecently = getCookie(LOYALTY_OPT_IN_ONBOARDING_DIALOG_COOKIE);
        const hasSeenFreeDeliveryModalRecently = getCookie(FREE_DELIVERY_SESSION_COOKIE) === 'true';

        const showOnboardingForEligible =
            isLoyaltyEligible &&
            !!dialogType &&
            noOtherModalIsShown &&
            !shouldShowFreeDeliveryModal &&
            !hasSeenOnboardingModalRecently &&
            !hasSeenFreeDeliveryModalRecently;

        return showOnboardingForEligible;
    }, [
        noOtherModalIsShown,
        dialogType,
        hasFreeDeliveryBenefit,
        isLoyaltyMember,
        isLoyaltyEligible,
        shouldShowFreeDeliveryModal,
    ]);

    const [showFreeDeliveryDialog, setShowFreeDeliveryDialog] = useState(false);

    useEffect(() => {
        if (shouldShowFreeDeliveryModal && !showFreeDeliveryDialog) {
            setShowFreeDeliveryDialog(true);
            setCookie(FREE_DELIVERY_SESSION_COOKIE, 'true', ONE_HOUR); // session cookie
        }
    }, [shouldShowFreeDeliveryModal]);

    useEffect(() => {
        if (shouldShowLoyaltyOnboardingScreen) {
            trackViewContent('loyalty_onboarding');
        }
    }, [shouldShowLoyaltyOnboardingScreen]);

    const [showExpiredCampaignDialog, setShowExpiredCampaignDialog] = useState(
        () =>
            !!query?.status &&
            !upcomingCampaigns.some(uc => uc.campaignId === query.event) &&
            query.event !== 'showstopper',
    );

    const expiredMessage =
        query?.status === 'upcoming' || query?.status === 'open'
            ? CampaignExpiredStatus.NOT_FOUND
            : CampaignExpiredStatus.EXPIRED;

    const handleExpiredDialogClose = useCallback(() => setShowExpiredCampaignDialog(false), []);

    const { shouldRenderGRECarousel, greData, greTabData, greCarouselCampaigns, isCyberWeek } =
        useGRE();

    const { ref: greInViewRef, inView: greInView } = useInView({
        threshold: 0.1,
        triggerOnce: true,
    });

    useEffect(() => {
        if (!greInView) return;

        trackViewPromotion({ element: 'gre_carousel', creativeName: 'gre_carousel', items: [] });
    }, [greInView]);

    const handleSeeMoreGRECampaigns = () => {
        if (!greTabData) return;

        trackSelectContent('gre_carousel', 'click', { contentId: 'see_all_deals_button' });
        navigate(`/event/${greTabData.id}`);
    };

    useEffect(() => {
        if (!window || !window.sessionStorage) {
            return;
        }
        const tabGenderValue = tabGender?.toUpperCase();
        if (tabGenderValue) {
            window.sessionStorage.setItem('selected_fashion_preference', tabGenderValue);
        } else {
            window.sessionStorage.removeItem('selected_fashion_preference');
        }
    });

    const handleCloseLoyaltyOnboardingScreen = () => {
        loyaltyOnboardingScreenShown();
        trackSelectContent('loyalty_onboarding', 'click', {
            contentId: 'close',
        });
    };

    const delayModalClose = (dialogType: PlusContentDialogType) => {
        sleep(300).then(() => {
            markDialogAsShown(dialogType);
        });
    };

    const getCustomerData = useCallback(async () => {
        await dispatch(fetchCustomer(true));
    }, []);

    const handleHashNavigation = async (postMessage: MessageEvent) => {
        if (postMessage?.data?.type === 'navigate') {
            switch (postMessage?.data?.hashbang) {
                case LATER_HASH: {
                    delayModalClose(dialogType as PlusContentDialogType);
                    break;
                }
                case PLUS_HASH: {
                    delayModalClose(dialogType as PlusContentDialogType);
                    window.open(`${fs_link}plus`, '_blank');
                    break;
                }
                case TERMS_AND_CONDITIONS_HASH: {
                    window.open(`${fs_link}zalando-terms-loyalty`, '_blank');
                    break;
                }
                case CONTINUE_SHOPPING_HASH: {
                    navigate(MYLOUNGE_ROUTE);
                    await getCustomerData();
                    delayModalClose(PlusContentDialogType.LOYALTY_OPT_IN_ONBOARDING);
                    delayModalClose(PlusContentDialogType.LOYALTY_OPT_IN_SUCCESS);
                    break;
                }
                case EXPLORE_YOUR_REWARDS_HASH: {
                    await getCustomerData();
                    delayModalClose(PlusContentDialogType.LOYALTY_OPT_IN_ONBOARDING);
                    delayModalClose(PlusContentDialogType.LOYALTY_OPT_IN_SUCCESS);
                    window.open(`${fs_link}plus`, '_blank');
                    break;
                }
                default: {
                    // Handle default case
                    break;
                }
            }
        }
    };

    const handleCloseLoyaltyModalM2 = async () => {
        await getCustomerData();
        if (loyalty.summary?.status === LoyaltyStatus.Active) {
            markDialogAsShown(PlusContentDialogType.LOYALTY_OPT_IN_ONBOARDING);
            markDialogAsShown(PlusContentDialogType.LOYALTY_OPT_IN_SUCCESS);

            trackSelectContent('loyalty_onboarding', 'click', {
                contentId: 'close',
            });
        } else {
            markDialogAsShown(dialogType as PlusContentDialogType);

            trackSelectContent('loyalty_onboarding', 'click', {
                contentId: 'close',
            });
        }
    };

    const handleCallToActionLoyaltyOnboardingScreen = () => {
        loyaltyOnboardingScreenShown();
        trackSelectContent('loyalty_onboarding', 'click', {
            contentId: 'got_it',
        });
    };

    const handleTurnOnPlusClick = () => {
        RestClient.post(`/api/phoenix/loyalty/consent`)
            .withHeaders({
                'X-Touchpoint': 'best-deals',
                [CSRF_HEADER]: getCSRFCookie(XSRF) || '',
            })
            .withPayload({ consent_status: GRANTED })
            .acceptStatusCodes([200, 204])
            .send()
            .then(() => {
                setShowFreeDeliveryDialog(false);

                setCookie(FREE_DELIVERY_COOKIE, 'true', ONE_DAY * 14); // 14 days

                dispatch(
                    showHeaderFlashMessage({
                        type: 'success',
                        message: translate(
                            'free_delivery.eligible.popup.turn_on_plus_success_description',
                            { minimumOrderValue },
                        ),
                    }),
                );
            })
            .catch(e => {
                console.error('Error in giving consent', e);
            });
    };

    const handleCloseFreeDeliveryDialog = async () => {
        setCookie(FREE_DELIVERY_COOKIE, 'true', ONE_DAY * 7); // 7 days
        setShowFreeDeliveryDialog(false);
    };

    return (
        <>
            <TranslateScope path="myLounge">
                <Helmet>
                    <title>{tabName}</title>
                </Helmet>
                <S.OuterWrapper id="main" tabIndex={-1}>
                    <AccessibleHeader tag={Tag.H1}>
                        {isPrive ? 'ZALANDO PRIVÉ' : 'ZALANDO LOUNGE'}
                    </AccessibleHeader>
                    <ExpiredCampaignDialog
                        show={showExpiredCampaignDialog}
                        onClose={handleExpiredDialogClose}
                        showStatus={expiredMessage}
                    />
                    {showCrossCampaignFilterInMobile && (
                        <CrossCampaignEntrySmallScreen categoryTabId={categoryTabId} />
                    )}
                    <ErrorBoundary componentName="MyLoungeBanner">
                        <Banner wrapperId={CAMPAIGN_WRAPPER} />
                    </ErrorBoundary>
                    {shouldRenderGRECarousel && (
                        <ErrorBoundary componentName="GRECarousel">
                            <S.GREWrapper ref={greInViewRef}>
                                <LUXGrid>
                                    <GridRow>
                                        <GridCol span={12}>
                                            <S.GREContentWrapper id="gre-carousel-title">
                                                <GRETitle
                                                    preset={TitlePreset.M}
                                                    color={FunctionColor.function0}
                                                    $isCyberWeek={isCyberWeek}
                                                >
                                                    {greData?.title}
                                                </GRETitle>
                                                <GREDescription
                                                    preset={TextPreset.M}
                                                    color={FunctionColor.function0}
                                                    $isCyberWeek={isCyberWeek}
                                                >
                                                    {greData?.content}
                                                </GREDescription>
                                            </S.GREContentWrapper>
                                        </GridCol>
                                    </GridRow>
                                </LUXGrid>
                                <S.GridWithBackground>
                                    <GridRow>
                                        <GridCol span={12}>
                                            <GRECarousel campaigns={greCarouselCampaigns} />
                                        </GridCol>
                                    </GridRow>
                                    <GridRow>
                                        <GridCol span={12}>
                                            <Button
                                                buttonType={ButtonType.secondary}
                                                color={ButtonColor.dark}
                                                fullWidth
                                                onClick={handleSeeMoreGRECampaigns}
                                            >
                                                {greData?.buttonText}
                                            </Button>
                                        </GridCol>
                                    </GridRow>
                                </S.GridWithBackground>
                            </S.GREWrapper>
                        </ErrorBoundary>
                    )}

                    <AccessibleHeader tag={Tag.H2} aria-live="assertive">
                        {tabName || ''}
                    </AccessibleHeader>
                    {flashMessages && flashMessages.length !== 0 && (
                        <GridPageLayout>
                            <S.InlineFlashMessage>
                                {flashMessages.map((message: FlashMessageWithText) => (
                                    <Message
                                        key={message?.messageId}
                                        type={MessageType.INLINE}
                                        purpose={convertFlashMessageToAlertType(message?.type)}
                                        title={message?.title}
                                        linkText={message?.action?.text}
                                        onLinkClick={() => {
                                            if (typeof message?.action?.callback === 'string') {
                                                window.location.href = message?.action?.callback;
                                            } else {
                                                message?.action?.callback();
                                            }
                                        }}
                                    >
                                        {message.text || message.message}
                                    </Message>
                                ))}
                            </S.InlineFlashMessage>
                        </GridPageLayout>
                    )}

                    <GridPageLayout>
                        <S.Wrapper id={CAMPAIGN_WRAPPER}>
                            {categoryTabId === CategoryTabIds.ALL &&
                                hasPlusOffersTab &&
                                isEACarouselEnabled && <EACarousel />}
                            <ErrorBoundary componentName="MyLoungeCampaigns">
                                <Grid campaigns={openCampaigns} isPlusEnabled={plus.isEnabled} />
                            </ErrorBoundary>
                            <HighlightsCarousel gender={gender} />
                        </S.Wrapper>
                    </GridPageLayout>
                    <ErrorBoundary componentName="UpcomingSection">
                        <GridPageLayout>
                            <TranslateScope path="upcoming">
                                <S.UpcomingTitle preset={TitlePreset.S} tag={Tag.H2}>
                                    <Translate message="upcomingCampaigns.upcomingTitle" />
                                </S.UpcomingTitle>
                            </TranslateScope>
                        </GridPageLayout>
                        <GridPageLayout>
                            <S.Wrapper $isLastContainer id={UPCOMING_CAMPAIGNS_WRAPPER}>
                                <UpcomingSection />
                            </S.Wrapper>
                        </GridPageLayout>
                    </ErrorBoundary>
                    <BottomBubble
                        customer={
                            customer as {
                                firstName: CustomerState['firstName'];
                                lastName: CustomerState['lastName'];
                                customerNumber: CustomerState['customerNumber'];
                                email: CustomerState['email'];
                            }
                        }
                        loyaltyEAVariant={false}
                    />
                </S.OuterWrapper>
            </TranslateScope>
            <TranslateScope path="plus">
                <LoyaltyOnboardingScreen
                    isShown={shouldShowLoyaltyOnboardingScreen}
                    onClose={handleCloseLoyaltyOnboardingScreen}
                    onCallToAction={handleCallToActionLoyaltyOnboardingScreen}
                />

                <PlusContentDialog
                    onMessage={handleHashNavigation}
                    onClose={handleCloseLoyaltyModalM2}
                    isShown={shouldShowLoyaltyOnboardingModal}
                    src={window.location.origin + dialogUrl}
                />
            </TranslateScope>
            <FreeDeliveryBenefitsDialog
                open={showFreeDeliveryDialog}
                onClose={handleCloseFreeDeliveryDialog}
                onTurnOnPlusClick={handleTurnOnPlusClick}
                isLoyaltyMember={isLoyaltyMember}
                minimumOrderValue={minimumOrderValue}
            />
        </>
    );
};

export default MyLoungeWrapper;
